import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Tables } from "@/integrations/supabase/types";

export type Restaurant = Tables<"restaurants">;
export type Category = Tables<"categories">;
export type DbProduct = Tables<"products">;
export type DbOrder = Tables<"orders">;

/** @deprecated Use DbProduct instead */
export type DbMenuItem = DbProduct;

export function useRestaurant() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetch = async () => {
      const { data } = await supabase
        .from("restaurants")
        .select("*")
        .eq("owner_id", user.id)
        .single();
      setRestaurant(data);
      setLoading(false);
    };
    fetch();
  }, [user]);

  const updateRestaurant = async (updates: Partial<Restaurant>) => {
    if (!restaurant) return;
    const { data, error } = await supabase
      .from("restaurants")
      .update(updates)
      .eq("id", restaurant.id)
      .select()
      .single();
    if (!error && data) setRestaurant(data);
    return { data, error };
  };

  return { restaurant, loading, updateRestaurant };
}

export function useCategories(restaurantId: string | undefined) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    if (!restaurantId) return;
    const { data } = await supabase
      .from("categories")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("sort_order");
    setCategories(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, [restaurantId]);

  const addCategory = async (cat: { name: string; name_ar?: string; icon?: string }) => {
    if (!restaurantId) return;
    const { error } = await supabase.from("categories").insert({ ...cat, restaurant_id: restaurantId });
    if (!error) fetchCategories();
    return { error };
  };

  const deleteCategory = async (id: string) => {
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (!error) fetchCategories();
  };

  return { categories, loading, addCategory, deleteCategory, refetch: fetchCategories };
}

export function useMenuItems(restaurantId: string | undefined) {
  const [items, setItems] = useState<DbProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    if (!restaurantId) return;
    const { data } = await supabase
      .from("products")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("sort_order");
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, [restaurantId]);

  const addItem = async (item: any) => {
    if (!restaurantId) return;
    const { error } = await supabase.from("products").insert({ ...item, restaurant_id: restaurantId });
    if (!error) fetchItems();
    return { error };
  };

  const updateItem = async (id: string, updates: any) => {
    const { error } = await supabase.from("products").update(updates).eq("id", id);
    if (!error) fetchItems();
    return { error };
  };

  const deleteItem = async (id: string) => {
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (!error) fetchItems();
  };

  return { items, loading, addItem, updateItem, deleteItem, refetch: fetchItems };
}

export function useOrders(restaurantId: string | undefined) {
  const [orders, setOrders] = useState<DbOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    if (!restaurantId) return;
    const { data } = await supabase
      .from("orders")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("created_at", { ascending: false });
    setOrders(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, [restaurantId]);

  const updateOrderStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("orders").update({ status }).eq("id", id);
    if (!error) fetchOrders();
    return { error };
  };

  return { orders, loading, updateOrderStatus, refetch: fetchOrders };
}
